import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertDialog, Button, Callout, Icon, Link, Spinner, Tab, TabGroup } from '@humanforce/mango-ui-react';
import { useSearchParams } from 'react-router';
import { getPayRunDetail, getPayRunExceptions, getPopulationStatus, requestPayRunReadiness } from './payRunPopulationHttpClient';
import {
  getPayRunCalculation,
  getPayRunFinalisation,
  getPayRunApproval,
  decidePayRunApproval,
  PayRunRequestError,
  retryPayRunCalculation,
  startPayRunCalculation,
  transitionPayRun,
  finalisePayRun,
  downloadPayRunAbaFile,
  retryPayRunFanOutLeg,
  recoverPayRunFinalisation,
  submitPayRunApproval,
  updatePayRun,
} from './payRunsHttpClient';
import type { PayRunApprovalDecision, PayRunApprovalDto, PayRunCalculationSummaryDto, PayRunDetailDto, PayRunFieldError, PayRunFinalisationDto } from './payRunsApiTypes';
import type {
  PayRunPopulationRefreshAccepted,
  PayRunPopulationStatus,
  PayRunExceptionsPage,
} from './payRunPopulationApiTypes';
import { PayRunDetailHeader, PayRunDetailSummary } from './PayRunDetailHeader';
import { PayRunDetailTitle } from './PayRunDetailTitle';
import type { PayRunTitleSecondaryAction } from './PayRunDetailTitle';
import { PayRunTimeline } from './PayRunTimeline';
import { buildPayRunSummaryLeadCards, buildPayRunTimeline, findPayRunPrimaryAction } from './payRunTimelineModel';
import { getBsbValidation, isNotEvaluated } from './bsbValidationHttpClient';
import type { BsbValidationDetails } from './bsbValidationApiTypes';
import { PAY_RUN_BANKING_SECTION_ID } from './PayRunBankingPanel';
import { isSafePayRunPresentationState } from './payRunsModel';
import { PayRunEmployeesTab } from './PayRunEmployeesTab';
import { PayRunExceptionsTab } from './PayRunExceptionsTab';
import { PayRunAuditTrailTab } from './PayRunAuditTrailTab';
import { CancelPayRunModal, EditPayRunModal } from './PayRunActionDialogs';
import { PayRunDataLoadModal } from './PayRunDataLoadModal';
import { PayRunApprovalPanel } from './PayRunApprovalPanel';
import { isEftLegAbaReady, PayRunFinalisationPanel } from './PayRunFinalisationPanel';
import { PayAdvicePreparationPanel } from './PayAdvicePreparationPanel';
import { downloadBlob } from './downloadBlob';
import { PayRunLifecyclePanel } from './PayRunLifecyclePanel';
import {
  approvalFingerprint,
  approvalPollInterval,
  calculationReloadIdentity,
  COMPLIANCE_STABILISATION_MAX_ATTEMPTS,
  complianceEvaluationStabilisationResult,
  exceptionEvaluationIdentity,
  hasActiveDownstreamLeg,
  isActiveCalculationStatus,
  isComplianceEvaluationStabilised,
  PAY_RUN_POLL_INTERVALS,
  shouldDiscoverPayRunDetail,
} from './payRunRefreshPolicy';

interface PayRunDetailScreenProps {
  payId: string;
  showHeaderAndLifecycle?: boolean;
}

interface ActionNotice {
  tone: 'positive' | 'critical';
  title: string;
  message: string;
  refreshRequired?: boolean;
}

type DetailTab = 'employees' | 'exceptions' | 'audit';

interface RefreshResult {
  payRun: PayRunDetailDto;
  status: PayRunPopulationStatus;
  exceptions: PayRunExceptionsPage;
  approval: PayRunApprovalDto | null;
}

const readTab = (value: string | null): DetailTab =>
  value === 'exceptions' || value === 'audit' ? value : 'employees';

// Mango 0.71.0 renders the AlertDialog heading without associating it to the
// alertdialog. Match the existing Pay Run modal bridge until Mango fixes it.
const useMangoAlertDialogHeadingBridge = (visible: boolean, testId: string) => {
  useEffect(() => {
    if (!visible) return undefined;
    const bindHeading = () => {
      const dialog = document.querySelector<HTMLElement>(`[data-testid="${testId}"][role="alertdialog"]`);
      const heading = dialog?.querySelector<HTMLElement>('h2');
      if (!dialog || !heading) return false;
      heading.id = `${testId}-heading`;
      dialog.setAttribute('aria-labelledby', heading.id);
      return true;
    };
    if (bindHeading()) return undefined;
    const observer = new MutationObserver(() => {
      if (bindHeading()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [testId, visible]);
};

export const PayRunDetailScreen = ({ payId, showHeaderAndLifecycle = true }: PayRunDetailScreenProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = readTab(searchParams.get('tab'));
  const [payRun, setPayRun] = useState<PayRunDetailDto | null>(null);
  const [status, setStatus] = useState<PayRunPopulationStatus | null>(null);
  const [exceptions, setExceptions] = useState<PayRunExceptionsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [activeDialog, setActiveDialog] = useState<'edit' | 'cancel' | 'load' | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<PayRunFieldError[]>([]);
  const [actionNotice, setActionNotice] = useState<ActionNotice | null>(null);
  const [displayLabel, setDisplayLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [populationReloadKey, setPopulationReloadKey] = useState(0);
  const [readinessReloadKey, setReadinessReloadKey] = useState(0);
  const [banking, setBanking] = useState<BsbValidationDetails | null>(null);
  const [bankingLoading, setBankingLoading] = useState(true);
  const [bankingError, setBankingError] = useState<string | null>(null);
  const [bankingNotEvaluated, setBankingNotEvaluated] = useState(false);
  const [pendingBankingFocus, setPendingBankingFocus] = useState(false);
  const bankingRequest = useRef<AbortController | null>(null);
  const [calculationBusy, setCalculationBusy] = useState(false);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [approval, setApproval] = useState<PayRunApprovalDto | null>(null);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [approvalErrorKind, setApprovalErrorKind] = useState<'action' | 'load' | null>(null);
  const [finalisationBusy, setFinalisationBusy] = useState(false);
  const [finalisationDialogOpen, setFinalisationDialogOpen] = useState(false);
  const [fanOutBusyLeg, setFanOutBusyLeg] = useState<string | null>(null);
  const [abaBusy, setAbaBusy] = useState(false);
  // A ref as well as state: two calls dispatched in the same tick (the automatic download racing a fast manual
  // click) would both pass a state-based guard before React re-renders.
  const abaInFlight = useRef(false);
  const [abaNotice, setAbaNotice] = useState<ActionNotice | null>(null);
  const [finalisationError, setFinalisationError] = useState<string | null>(null);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== 'hidden');
  const [complianceBaseline, setComplianceBaseline] = useState<string | null>(null);
  const complianceStabilisationAttempts = useRef(0);
  const refreshInFlight = useRef<Promise<RefreshResult | null> | null>(null);
  const refreshInFlightSignal = useRef<AbortSignal | null>(null);
  const refreshGeneration = useRef(0);
  const calculationVersion = useRef(0);
  const approvalVersion = useRef(0);
  // A 409 conflict reason from submit/decide is the server's actionable explanation for refusing the action.
  // Background refreshes (loadHeader, refreshSurface and the approval poller) all clear approvalError, which is what
  // erased it before. The 409 reason -- and only the 409 reason; other action failures are not retained -- is held
  // here, scoped to the exact Pay Run and approval context it was raised against, so a refresh cannot silently drop
  // it while that context is still current, and it stops applying as soon as the context changes (for example after
  // Unlock to Compliance freezes a fresh one).
  const stickyApprovalConflict = useRef<{ message: string; payId: string; contextId: string; calculationHash: string } | null>(null);
  const forgetApprovalConflict = useCallback(() => { stickyApprovalConflict.current = null; }, []);
  const retainedApprovalConflict = useCallback((next: PayRunApprovalDto | null) => {
    const sticky = stickyApprovalConflict.current;
    if (!sticky || sticky.payId !== payId || !next) return null;
    if (next.contextId !== sticky.contextId || next.calculationHash !== sticky.calculationHash) {
      stickyApprovalConflict.current = null;
      return null;
    }
    return sticky.message;
  }, [payId]);
  const finalisationVersion = useRef(0);
  const pendingEmployeeReload = useRef(false);
  const pendingComplianceReload = useRef(false);
  const employeeReloadIdentity = useRef<string | null>(null);
  const pendingEmployeeReloadCalculation = useRef<PayRunCalculationSummaryDto | null>(null);
  const commitEmployeeReloadIdentity = (calculation?: PayRunCalculationSummaryDto | null): boolean => {
    const identity = calculationReloadIdentity(calculation);
    if (identity == null) return false;
    if (employeeReloadIdentity.current === identity) return false;
    employeeReloadIdentity.current = identity;
    return true;
  };
  const lastFocusRefreshAt = useRef(0);
  const payRunRef = useRef<PayRunDetailDto | null>(null);
  const approvalRef = useRef<PayRunApprovalDto | null>(null);
  const exceptionsRef = useRef<PayRunExceptionsPage | null>(null);
  // PRR-317: the ABA download surface appears only where EFT actually applies to this Pay Run.
  const eftApplies = payRun?.eftApplicability === 'applicable';
  const actionLocked = actionBusy || Boolean(actionNotice?.refreshRequired);
  const lifecycleBusy = transitioning || actionLocked || approvalBusy || finalisationBusy;
  const actionFocusTarget = useRef<'header' | 'lifecycle' | null>(null);
  const bankingInput = useMemo(
    () => ({ details: banking, loading: bankingLoading, failed: bankingError !== null }),
    [banking, bankingLoading, bankingError],
  );
  const timeline = useMemo(
    () => (payRun ? buildPayRunTimeline({ payRun, approval, exceptions, banking: bankingInput }) : null),
    [payRun, approval, exceptions, bankingInput],
  );
  const safeState = payRun ? isSafePayRunPresentationState(payRun) : false;
  const leadCards = useMemo(
    () => (timeline ? buildPayRunSummaryLeadCards(timeline, exceptions) : []),
    [timeline, exceptions],
  );
  // The action beside the run's name is the same one the timeline's own step menu offers,
  // so it is read from the model rather than re-derived from the DTO — every one of them
  // is already gated on a server projection.
  const primaryAction = useMemo(
    () => safeState && timeline
      ? findPayRunPrimaryAction(timeline)
      ?? (payRun?.meta.actions.includes('make_payroll_active')
        ? { verb: 'make_payroll_active' as const, label: 'Make payroll active' }
        : null)
      : null,
    [payRun, safeState, timeline],
  );
  const secondaryActions = useMemo<PayRunTitleSecondaryAction[]>(() => {
    if (!payRun || !safeState) return [];
    const actions: PayRunTitleSecondaryAction[] = [];
    if (payRun.meta.actions.includes('edit'))
      actions.push({ verb: 'edit', label: 'Edit' });
    if (payRun.meta.actions.includes('revert_to_trial'))
      actions.push({ verb: 'revert_to_trial', label: 'Revert to trial' });
    if (payRun.meta.actions.includes('unlock_to_compliance'))
      actions.push({ verb: 'unlock_to_compliance', label: 'Unlock to compliance' });
    if (payRun.meta.actions.includes('cancel'))
      actions.push({ verb: 'cancel', label: 'Cancel pay run', destructive: true });
    return actions;
  }, [payRun, safeState]);
  const hasApprovalDecision = approval?.streams.some((stream) =>
    stream.requirements.some((requirement) => requirement.canApprove || requirement.canReject)) ?? false;
  // A context that goes stale BEFORE submission has no submittedAt and no actionable requirement, but the operator
  // still needs the explanation and the named recovery -- otherwise Submit just silently disappears.
  const approvalPanelVisible = Boolean(safeState && approval && (approval.submittedAt || hasApprovalDecision
    || (approval.isCalculationStale && payRun?.workflowStatus === 'ready_to_pay')));
  useMangoAlertDialogHeadingBridge(finalisationDialogOpen, 'finalise-pay-run-dialog');

  useEffect(() => {
    stickyApprovalConflict.current = null;
    setApprovalError(null);
    setApprovalErrorKind(null);
    employeeReloadIdentity.current = null;
  }, [payId]);

  useEffect(() => { payRunRef.current = payRun; }, [payRun]);
  useEffect(() => { approvalRef.current = approval; }, [approval]);
  useEffect(() => { exceptionsRef.current = exceptions; }, [exceptions]);

  const refreshSurface = useCallback((options: {
    signal?: AbortSignal;
    reloadEmployees?: boolean;
    employeeReloadCalculation?: PayRunCalculationSummaryDto | null;
    reloadCompliance?: boolean;
    force?: boolean;
  } = {}): Promise<RefreshResult | null> => {
    if (refreshInFlight.current && !refreshInFlightSignal.current?.aborted) {
      pendingEmployeeReload.current ||= Boolean(options.reloadEmployees);
      if (options.employeeReloadCalculation !== undefined)
        pendingEmployeeReloadCalculation.current = options.employeeReloadCalculation;
      pendingComplianceReload.current ||= Boolean(options.reloadCompliance);
      if (!options.force) return refreshInFlight.current;
      const current = refreshInFlight.current;
      return current.catch(() => null).then(() => refreshSurface({ ...options, force: false }));
    }
    const generation = ++refreshGeneration.current;
    const promise = (async () => {
      const [nextPayRun, nextStatus, nextExceptions] = await Promise.all([
        getPayRunDetail(payId, options.signal),
        getPopulationStatus(payId, options.signal),
        getPayRunExceptions(payId, { page: 1, pageSize: 1 }, options.signal),
      ]);
      let nextApproval: PayRunApprovalDto | null = null;
      if (['ready_to_pay', 'eft_ready', 'cancelled'].includes(nextPayRun.workflowStatus)) {
        try {
          nextApproval = await getPayRunApproval(payId, options.signal);
          // A superseded or aborted refresh must not write approval error state; its result belongs to a Pay Run or
          // a generation the screen has already moved on from.
          if (generation === refreshGeneration.current && !options.signal?.aborted) {
            const retained = retainedApprovalConflict(nextApproval);
            setApprovalError(retained);
            setApprovalErrorKind(retained ? 'action' : null);
          }
        } catch (approvalFailure) {
          const superseded = generation !== refreshGeneration.current || options.signal?.aborted === true;
          if (!superseded && (!(approvalFailure instanceof PayRunRequestError) || approvalFailure.status !== 409)) {
            setApprovalError('Approval details could not be loaded. Refresh the Pay Run before taking an approval action.');
            setApprovalErrorKind('load');
          }
        }
      }
      if (generation !== refreshGeneration.current || options.signal?.aborted) return null;
      calculationVersion.current += 1;
      approvalVersion.current += 1;
      finalisationVersion.current += 1;
      setPayRun(nextPayRun);
      setStatus(nextStatus);
      setExceptions(nextExceptions);
      setApproval(nextApproval);
      const calculationForReload = options.employeeReloadCalculation !== undefined
        ? options.employeeReloadCalculation
        : pendingEmployeeReloadCalculation.current;
      pendingEmployeeReloadCalculation.current = null;
      const calculationReloadNeeded = commitEmployeeReloadIdentity(calculationForReload);
      if (options.reloadEmployees || pendingEmployeeReload.current || calculationReloadNeeded) {
        pendingEmployeeReload.current = false;
        setPopulationReloadKey((current) => current + 1);
      }
      if (options.reloadCompliance || pendingComplianceReload.current) {
        pendingComplianceReload.current = false;
        setReadinessReloadKey((current) => current + 1);
      }
      return { payRun: nextPayRun, status: nextStatus, exceptions: nextExceptions, approval: nextApproval };
    })().finally(() => {
      if (refreshInFlight.current === promise) {
        refreshInFlight.current = null;
        refreshInFlightSignal.current = null;
      }
    });
    refreshInFlight.current = promise;
    refreshInFlightSignal.current = options.signal ?? null;
    return promise;
  }, [payId, retainedApprovalConflict]);

  const loadHeader = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    setApprovalError(null);
    setApprovalErrorKind(null);
    try {
      await refreshSurface({ signal });
    } catch (loadError) {
      if (!signal?.aborted) setError(loadError instanceof Error ? loadError.message : 'Pay Run details could not be loaded.');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [refreshSurface]);

  useEffect(() => {
    const controller = new AbortController();
    void loadHeader(controller.signal);
    return () => controller.abort();
  }, [loadHeader]);

  useEffect(() => {
    if (status?.loadStatus !== 'loading') return undefined;
    const timer = window.setInterval(() => {
      void getPopulationStatus(payId).then((nextStatus) => {
        setStatus(nextStatus);
        if (nextStatus.loadStatus !== 'loading') {
          void getPayRunDetail(payId).then(setPayRun).catch(() => undefined);
        }
      }).catch(() => undefined);
    }, 1500);
    return () => window.clearInterval(timer);
  }, [payId, status?.loadStatus]);

  useEffect(() => {
    const calculation = payRun?.calculation;
    if (!pageVisible || !calculation?.attemptId || !isActiveCalculationStatus(calculation.status)) return undefined;
    const attemptId = calculation.attemptId;
    const controller = new AbortController();
    let timer: number | undefined;
    const poll = () => {
      const observedVersion = calculationVersion.current;
      void getPayRunCalculation(payId, attemptId, controller.signal)
        .then(async (nextCalculation) => {
          if (controller.signal.aborted) return;
          if (observedVersion !== calculationVersion.current) {
            const current = payRunRef.current?.calculation;
            if (current?.attemptId === attemptId && isActiveCalculationStatus(current.status))
              timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.calculation);
            return;
          }
          setCalculationError(null);
          calculationVersion.current += 1;
          if (!isActiveCalculationStatus(nextCalculation.status)) {
            const baseline = exceptionEvaluationIdentity(exceptionsRef.current);
            setPayRun((current) => current ? { ...current, calculation: nextCalculation } : current);
            const result = await refreshSurface({
              employeeReloadCalculation: nextCalculation,
              reloadCompliance: true,
              force: true,
            });
            if (nextCalculation.status === 'complete'
              && (!result || !isComplianceEvaluationStabilised(baseline, result.exceptions))) {
              complianceStabilisationAttempts.current = 0;
              setComplianceBaseline(baseline);
            }
          } else {
            setPayRun((current) => current ? { ...current, calculation: nextCalculation } : current);
            timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.calculation);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setCalculationError('Calculation status could not be refreshed. Retry the status check.');
            timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.calculation);
          }
        });
    };
    timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.calculation);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      controller.abort();
    };
  }, [pageVisible, payId, payRun?.calculation?.attemptId, payRun?.calculation?.status, refreshSurface]);

  useEffect(() => {
    if (!pageVisible || !shouldDiscoverPayRunDetail(payRun?.workflowStatus, payRun?.calculation?.status)) return undefined;
    const controller = new AbortController();
    let timer: number | undefined;
    const poll = () => {
      const observedGeneration = refreshGeneration.current;
      void getPayRunDetail(payId, controller.signal).then((nextPayRun) => {
        if (controller.signal.aborted) return;
        const previous = payRunRef.current;
        const workflowStatusChanged = previous?.workflowStatus !== nextPayRun.workflowStatus;
        const attemptIdChanged = previous?.calculation?.attemptId !== nextPayRun.calculation?.attemptId;
        if (observedGeneration === refreshGeneration.current && (workflowStatusChanged || attemptIdChanged)) {
          void refreshSurface({
            reloadEmployees: workflowStatusChanged,
            employeeReloadCalculation: nextPayRun.calculation,
            reloadCompliance: true,
            force: true,
          }).catch(() => undefined);
        }
        timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.detailDiscovery);
      }).catch(() => {
        if (!controller.signal.aborted)
          timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.detailDiscovery);
      });
    };
    timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.detailDiscovery);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      controller.abort();
    };
  }, [pageVisible, payId, payRun?.calculation?.status, payRun?.workflowStatus, refreshSurface]);

  useEffect(() => {
    if (!pageVisible || complianceBaseline == null) return undefined;
    const controller = new AbortController();
    let timer: number | undefined;
    const stop = () => {
      setComplianceBaseline((current) => current === complianceBaseline ? null : current);
    };
    const poll = () => {
      complianceStabilisationAttempts.current += 1;
      void refreshSurface({
        signal: controller.signal,
        employeeReloadCalculation: payRunRef.current?.calculation,
        reloadCompliance: true,
      })
        .then((result) => {
          if (controller.signal.aborted) return;
          const outcome = result
            ? complianceEvaluationStabilisationResult(complianceBaseline, result.exceptions)
            : 'continue';
          if (outcome !== 'continue'
            || complianceStabilisationAttempts.current >= COMPLIANCE_STABILISATION_MAX_ATTEMPTS) {
            stop();
            return;
          }
          timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.complianceStabilisation);
        }).catch(() => {
          if (controller.signal.aborted) return;
          if (complianceStabilisationAttempts.current >= COMPLIANCE_STABILISATION_MAX_ATTEMPTS) stop();
          else timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.complianceStabilisation);
        });
    };
    timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.complianceStabilisation);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      controller.abort();
    };
  }, [complianceBaseline, pageVisible, refreshSurface]);

  useEffect(() => {
    const interval = approvalPollInterval(payRun?.workflowStatus, approval);
    if (!pageVisible || interval === false) return undefined;
    const controller = new AbortController();
    let timer: number | undefined;
    const poll = () => {
      const previousFingerprint = approvalRef.current ? approvalFingerprint(approvalRef.current) : null;
      const observedVersion = approvalVersion.current;
      void getPayRunApproval(payId, controller.signal).then((nextApproval) => {
        if (controller.signal.aborted) return;
        if (observedVersion !== approvalVersion.current) {
          if (payRunRef.current?.workflowStatus === 'ready_to_pay')
            timer = window.setTimeout(poll, interval);
          return;
        }
        approvalVersion.current += 1;
        setApproval(nextApproval);
        if (approvalFingerprint(nextApproval) !== previousFingerprint) {
          void refreshSurface({ reloadCompliance: true, force: true }).catch(() => undefined);
        }
        timer = window.setTimeout(poll, interval);
      }).catch(() => {
        if (!controller.signal.aborted) timer = window.setTimeout(poll, interval);
      });
    };
    timer = window.setTimeout(poll, interval);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      controller.abort();
    };
  }, [approval, pageVisible, payId, payRun?.workflowStatus, refreshSurface]);

  useEffect(() => {
    if (!pageVisible || payRun?.workflowStatus !== 'complete' || !hasActiveDownstreamLeg(payRun.finalisation)) return undefined;
    const controller = new AbortController();
    let timer: number | undefined;
    const poll = () => {
      const observedVersion = finalisationVersion.current;
      void getPayRunFinalisation(payId, controller.signal)
        .then((finalisation) => {
          if (!controller.signal.aborted && observedVersion !== finalisationVersion.current) {
            if (hasActiveDownstreamLeg(payRunRef.current?.finalisation))
              timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.downstream);
            return;
          }
          if (!controller.signal.aborted) {
            finalisationVersion.current += 1;
            setPayRun((current) => current ? { ...current, finalisation } : current);
            if (hasActiveDownstreamLeg(finalisation))
              timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.downstream);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted)
            timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.downstream);
        });
    };
    timer = window.setTimeout(poll, PAY_RUN_POLL_INTERVALS.downstream);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      controller.abort();
    };
  }, [pageVisible, payId, payRun?.finalisation, payRun?.workflowStatus]);

  useEffect(() => {
    const refreshOnReturn = () => {
      const visible = document.visibilityState !== 'hidden';
      setPageVisible(visible);
      if (!visible) return;
      const now = Date.now();
      if (now - lastFocusRefreshAt.current < PAY_RUN_POLL_INTERVALS.focusThrottle) return;
      lastFocusRefreshAt.current = now;
      void refreshSurface({ reloadEmployees: true, reloadCompliance: true }).catch(() => undefined);
    };
    document.addEventListener('visibilitychange', refreshOnReturn);
    window.addEventListener('focus', refreshOnReturn);
    return () => {
      document.removeEventListener('visibilitychange', refreshOnReturn);
      window.removeEventListener('focus', refreshOnReturn);
    };
  }, [refreshSurface]);

  // Banking (BSB) evidence is read once here and shared by the Review timeline stage and the Compliance tab's
  // Banking panel, so the two can never disagree. Read-only: the endpoint always returns 200, with `state` telling
  // apart "not yet evaluated" (a normal low-noise state) from a real evidence read. Each load aborts any read still
  // in flight so a slow earlier response cannot overwrite a newer one.
  const bankingBusinessRecordId = payRun?.businessRecordId ?? null;
  const loadBanking = useCallback(async () => {
    if (!bankingBusinessRecordId) {
      // Terminal state, not a silent spin: without a Business Record there is nothing to read, and leaving
      // bankingLoading true would strand the panel on a spinner with no error and no Retry.
      setBankingLoading(false);
      return;
    }
    bankingRequest.current?.abort();
    const controller = new AbortController();
    bankingRequest.current = controller;
    setBankingLoading(true);
    try {
      const response = await getBsbValidation(bankingBusinessRecordId, payId, controller.signal);
      if (controller.signal.aborted) return;
      if (isNotEvaluated(response)) {
        // The state is authoritative: the Pay Run genuinely has no current evidence, so any previously held
        // evidence is now wrong and must be dropped.
        setBanking(null);
        setBankingNotEvaluated(true);
        setBankingError(null);
      } else if (response.state === 'evaluated' && response.details != null) {
        setBanking(response.details);
        setBankingNotEvaluated(false);
        setBankingError(null);
      } else {
        // `state` permits unknown/forward-version values and `details` is nullable, so a malformed or
        // not-yet-understood 200 must not be presented as healthy "no findings" evidence. Keep whatever
        // evidence was last known good and surface this as a contract/read failure, same as a thrown error.
        setBankingNotEvaluated(false);
        setBankingError('Banking (BSB) verification response could not be understood.');
      }
    } catch (failure) {
      if (controller.signal.aborted) return;
      // A transient read failure must not erase findings the operator may be acting on. Keep the last known
      // evidence and report the failure alongside it; the panel shows a "last known" caution plus Retry.
      setBankingNotEvaluated(false);
      setBankingError(failure instanceof Error
        ? failure.message
        : 'Banking (BSB) verification could not be loaded.');
    } finally {
      if (!controller.signal.aborted) setBankingLoading(false);
    }
  }, [bankingBusinessRecordId, payId]);

  useEffect(() => {
    void loadBanking();
    return () => bankingRequest.current?.abort();
  }, [loadBanking, readinessReloadKey]);

  /**
   * Approval and finalisation both revalidate BSB evidence server-side, so either can fail because new Banking
   * findings were just created. The refusal itself does not say so reliably, and matching on message text would be
   * brittle, so any failure of those actions re-reads the shared evidence. The read is cheap and read-only, and a
   * redundant one after an unrelated failure is far better than leaving the operator looking at stale successful
   * evidence while the backend holds a new blocker.
   */
  const refreshBankingAfterFailedProgression = useCallback(async (failure: unknown) => {
    // An auth or session failure says nothing about BSB evidence, and the follow-up read would fail the same
    // way — turning a healthy Banking panel into a second, misleading error surface next to the real problem.
    if (failure instanceof PayRunRequestError && (failure.status === 401 || failure.status === 403)) return;
    try {
      await loadBanking();
    } catch {
      // The originating action's error is the actionable one; never mask it with a refresh failure.
    }
  }, [loadBanking]);

  // Completes the timeline's "Review banking findings" hand-off once the Compliance tab has actually rendered.
  // Scrolling alone is not enough: keyboard and screen-reader users would otherwise stay on the timeline button.
  useEffect(() => {
    if (!pendingBankingFocus || activeTab !== 'exceptions') return;
    const section = document.getElementById(PAY_RUN_BANKING_SECTION_ID);
    if (!section) return;
    section.scrollIntoView({ block: 'start' });
    section.focus();
    setPendingBankingFocus(false);
  }, [pendingBankingFocus, activeTab]);

  if (loading) {
    return (
      <div
        className="mgo-cluster mgo-justify-center"
        aria-busy="true"
        aria-label="Loading Pay Run details"
        style={{ minHeight: '15rem', padding: 'var(--mgo-space-60)' }}
      >
        <span style={{ fontSize: 'var(--mgo-size-80)' }}><Spinner /></span>
      </div>
    );
  }

  if (error || !payRun) {
    return (
      <div className="mgo-stack mgo-gap-l" style={{ padding: 'var(--mgo-space-60)' }}>
        <Link href="/payroll/pay-runs">Back to Pay Runs</Link>
        <Callout
          tone="critical"
          title="Pay Run details unavailable"
          action={<Button label="Retry" size="small" variant="secondary" onClick={() => void loadHeader()} />}
        >
          {error}
        </Callout>
      </div>
    );
  }

  const changeTab = (value: string) => {
    const next = readTab(value);
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'employees') nextParams.delete('tab'); else nextParams.set('tab', next);
    setSearchParams(nextParams, { replace: true });
  };

  // Routes the operator from the Review timeline stage to the Banking section of the existing Compliance tab.
  // Reuses the existing URL-driven tab mechanism; it does not introduce a second navigation model.
  const openBankingFindings = () => {
    changeTab('exceptions');
    // Deferred to an effect rather than a rAF callback: the Compliance tab's children are not committed yet at
    // this point, so the section would not exist to scroll to or focus.
    setPendingBankingFocus(true);
  };

  const refreshAccepted = (accepted: PayRunPopulationRefreshAccepted) => {
    setStatus((current) => ({
      ...(current ?? {
        isActive: false,
        blockingIssueCount: 0,
        warningIssueCount: 0,
        reviewRequiredIssueCount: 0,
      }),
      populationRevisionId: accepted.populationRevisionId,
      revisionNumber: accepted.revisionNumber,
      loadStatus: accepted.loadStatus,
    }));
  };

  const applyTransition = async (verb: 'make_payroll_active' | 'revert_to_trial' | 'unlock_to_compliance') => {
    if (lifecycleBusy) return;
    setTransitioning(true);
    setTransitionError(null);
    try {
      const transitioned = await transitionPayRun(payId, verb);
      // PRR-321. The transition restarts downstream progress, so a client-local finalisation failure
      // from the superseded attempt is no longer current. Cleared only after the transition succeeds,
      // so a failed transition never hides a live failure. A server-projected finalisation/fan-out
      // failure is unaffected: it renders from `payRun.finalisation`, which is replaced below.
      setFinalisationError(null);
      setFinalisationDialogOpen(false);
      setPayRun(transitioned);
      await refreshSurface({ reloadEmployees: true, reloadCompliance: true, force: true });
    } catch (transitionFailure) {
      setTransitionError(transitionFailure instanceof Error
        ? transitionFailure.message
        : 'The Pay Run could not be transitioned.');
    } finally {
      setTransitioning(false);
    }
  };

  const reloadPayRun = async () => {
    const refreshed = await getPayRunDetail(payId);
    setPayRun(refreshed);
    return refreshed;
  };

  const reloadAfterDataLoad = async () => {
    try {
      await refreshSurface({ reloadEmployees: true, reloadCompliance: true, force: true });
    } catch {
      setActionNotice({
        tone: 'critical',
        title: 'Refresh required',
        message: 'The Pay Run data was loaded, but current details could not be refreshed.',
        refreshRequired: true,
      });
    }
  };

  const restoreActionFocus = () => {
    const target = actionFocusTarget.current;
    actionFocusTarget.current = null;
    window.setTimeout(() => {
      const trigger = target === 'header'
        ? document.querySelector<HTMLButtonElement>('[data-testid="pay-run-detail-secondary-actions"] button')
        : document.querySelector<HTMLButtonElement>('[data-testid="pay-run-lifecycle-actions"] button');
      if (trigger) trigger.focus();
      else document.querySelector<HTMLElement>('[data-testid="pay-run-action-result"]')?.focus();
    }, 350);
  };

  const closeDialog = () => {
    if (actionBusy) return;
    setActiveDialog(null);
    setActionError(null);
    setFieldErrors([]);
    restoreActionFocus();
  };

  const openEdit = () => {
    if (lifecycleBusy) return;
    actionFocusTarget.current = 'header';
    setDisplayLabel(payRun.displayLabel ?? '');
    setNotes(payRun.notes ?? '');
    setActionError(null);
    setFieldErrors([]);
    setActionNotice(null);
    setActiveDialog('edit');
  };

  const openCancel = (focusTarget: 'header' | 'lifecycle' = 'lifecycle') => {
    if (lifecycleBusy) return;
    actionFocusTarget.current = focusTarget;
    setActionError(null);
    setFieldErrors([]);
    setActionNotice(null);
    setActiveDialog('cancel');
  };

  const safeActionError = (failure: unknown, preserveConflictReason = false) => {
    if (!(failure instanceof PayRunRequestError)) return 'Please try again. If the problem continues, contact support.';
    if (failure.status === 401) return 'Your session has expired. Sign in again before retrying.';
    if (failure.status === 403) return 'You no longer have permission to perform this action.';
    if (failure.status === 409) return preserveConflictReason
      ? failure.message
      : 'The Pay Run changed before this action completed. Review its current state before retrying.';
    if (failure.status >= 500) return 'The Pay Run service is temporarily unavailable. Try again when it is available.';
    return failure.fieldErrors.length > 0 ? 'Review the highlighted fields and try again.' : 'The request could not be completed.';
  };

  const saveEdit = async () => {
    if (actionBusy || !payRun.meta.actions.includes('edit') || displayLabel.length > 200 || notes.length > 500) return;
    setActionBusy(true);
    setActionNotice(null);
    setActionError(null);
    setFieldErrors([]);
    try {
      await updatePayRun(payId, {
        displayLabel: displayLabel.trim() || null,
        notes: notes.trim() || null,
      });
      try {
        await reloadPayRun();
        setActionNotice({ tone: 'positive', title: 'Pay Run updated', message: 'The current Pay Run details are displayed.' });
      } catch {
        setActionNotice({
          tone: 'critical',
          title: 'Pay Run updated; refresh required',
          message: 'The Pay Run was updated, but current details could not be refreshed.',
          refreshRequired: true,
        });
      }
      setActiveDialog(null);
      restoreActionFocus();
    } catch (failure) {
      if (failure instanceof PayRunRequestError) {
        setFieldErrors(failure.fieldErrors);
        if (failure.status === 409) {
          try { await reloadPayRun(); } catch { /* retain the actionable write error */ }
        }
      }
      setActionError(safeActionError(failure));
    } finally {
      setActionBusy(false);
    }
  };

  const confirmCancel = async () => {
    if (actionBusy || !payRun.meta.actions.includes('cancel')) return;
    setActionBusy(true);
    setActionNotice(null);
    setActionError(null);
    try {
      await transitionPayRun(payId, 'cancel');
      try {
        await reloadPayRun();
        setActionNotice({ tone: 'positive', title: 'Pay Run cancelled', message: 'The retained Pay Run details are displayed.' });
      } catch {
        setActionNotice({
          tone: 'critical',
          title: 'Pay Run cancelled; refresh required',
          message: 'The Pay Run was cancelled, but current details could not be refreshed.',
          refreshRequired: true,
        });
      }
      setActiveDialog(null);
      restoreActionFocus();
    } catch (failure) {
      if (failure instanceof PayRunRequestError && failure.status === 409) {
        try { await reloadPayRun(); } catch { /* retain the actionable write error */ }
      }
      setActionError(safeActionError(failure));
    } finally {
      setActionBusy(false);
    }
  };

  const retryActionRefresh = async () => {
    if (actionBusy || transitioning) return;
    setActionBusy(true);
    try {
      await reloadPayRun();
      setActionNotice({ tone: 'positive', title: 'Pay Run details refreshed', message: 'The current server state is displayed.' });
    } catch {
      setActionNotice((current) => current ?? {
        tone: 'critical',
        title: 'Refresh required',
        message: 'Current Pay Run details could not be refreshed.',
        refreshRequired: true,
      });
    } finally {
      setActionBusy(false);
      restoreActionFocus();
    }
  };

  const calculate = async () => {
    if (calculationBusy) return;
    setCalculationBusy(true);
    setCalculationError(null);
    try {
      const current = payRun.calculation;
      const next = current?.attemptId && ['failed', 'review_required'].includes(current.status)
        ? await retryPayRunCalculation(payId, current.attemptId)
        : await startPayRunCalculation(payId, crypto.randomUUID());
      setPayRun((value) => value ? { ...value, calculation: next } : value);
    } catch (failure) {
      setCalculationError(failure instanceof Error
        ? failure.message
        : 'The calculation could not be started.');
    } finally {
      setCalculationBusy(false);
    }
  };

  const refreshCalculation = async () => {
    const attemptId = payRun.calculation?.attemptId;
    if (!attemptId || calculationBusy) return;
    setCalculationBusy(true);
    setCalculationError(null);
    try {
      const calculation = await getPayRunCalculation(payId, attemptId);
      setPayRun((value) => value ? { ...value, calculation } : value);
      if (!isActiveCalculationStatus(calculation.status)) {
        const baseline = exceptionEvaluationIdentity(exceptionsRef.current);
        // employeeReloadCalculation keeps the shared reload identity in sync with this manual
        // refresh, so a poller observing the same attempt moments later does not redundantly
        // reload the grid again for what this action already reloaded.
        await refreshSurface({ reloadEmployees: true, employeeReloadCalculation: calculation, reloadCompliance: true, force: true });
        if (calculation.status === 'complete') setComplianceBaseline(baseline);
      }
    } catch {
      setCalculationError('Calculation status could not be refreshed. Retry the status check.');
    } finally {
      setCalculationBusy(false);
    }
  };

  const submitApproval = async () => {
    if (!approval || lifecycleBusy || !approval.canSubmit) return;
    setApprovalBusy(true);
    // Only forget the retained conflict once a new attempt actually starts; an invocation that early-returns must
    // leave a still-true conflict on screen.
    forgetApprovalConflict();
    setApprovalError(null);
    setApprovalErrorKind(null);
    try {
      setApproval(await submitPayRunApproval(payId, approval.calculationHash));
      await refreshSurface({ reloadCompliance: true, force: true });
    } catch (failure) {
      // Approval conflicts are authored by our own service and are actionable operator guidance (stale context,
      // out-of-order approval, missing BSB evidence), so the reason is preserved as the finalisation paths already do.
      const message = safeActionError(failure, true);
      setApprovalError(message);
      setApprovalErrorKind('action');
      if (failure instanceof PayRunRequestError && failure.status === 409 && approval) {
        // Bind the reason to the context it was raised against, then refresh. The refresh re-asserts it while that
        // context is still current and drops it once the context changes, so the banner can never become undismissable.
        stickyApprovalConflict.current = {
          message, payId, contextId: approval.contextId, calculationHash: approval.calculationHash,
        };
        // force: true so this is not collapsed into a refresh that is already in flight and may have passed its
        // approval block. It chains after any in-flight refresh and then re-enters, so it is a narrowing rather
        // than an absolute guarantee. The refresh evaluates retainedApprovalConflict against the context it
        // actually loaded, re-asserting the reason while that context is current and dropping it once it is not.
        // Re-asserting from the ref here instead would resurrect the message for an already-replaced context.
        await refreshSurface({ reloadCompliance: true, force: true });
      }
      void refreshBankingAfterFailedProgression(failure);
    } finally {
      setApprovalBusy(false);
    }
  };

  const requestReadiness = async () => {
    if (lifecycleBusy || !exceptions?.canRequestReadiness) return;
    setTransitioning(true);
    setTransitionError(null);
    try {
      await requestPayRunReadiness(payId);
      await refreshSurface({ reloadEmployees: true, reloadCompliance: true, force: true });
    } catch (failure) {
      setTransitionError(failure instanceof Error ? failure.message : 'Readiness could not be evaluated.');
      try { await loadHeader(); } catch { /* retain the actionable request error */ }
    } finally {
      setTransitioning(false);
    }
  };

  const decideApproval = async (requirementId: string, decision: PayRunApprovalDecision) => {
    if (!approval || lifecycleBusy) return;
    setApprovalBusy(true);
    // See submitApproval: forget only after the guard, so an early return cannot silently drop a live conflict.
    forgetApprovalConflict();
    setApprovalError(null);
    setApprovalErrorKind(null);
    try {
      const nextApproval = await decidePayRunApproval(payId, requirementId, decision, approval.calculationHash);
      setApproval(nextApproval);
      await refreshSurface({ reloadCompliance: true, force: true });
    } catch (failure) {
      // Approval conflicts are authored by our own service and are actionable operator guidance (stale context,
      // out-of-order approval, missing BSB evidence), so the reason is preserved as the finalisation paths already do.
      const message = safeActionError(failure, true);
      setApprovalError(message);
      setApprovalErrorKind('action');
      if (failure instanceof PayRunRequestError && failure.status === 409 && approval) {
        // Bind the reason to the context it was raised against, then refresh. The refresh re-asserts it while that
        // context is still current and drops it once the context changes, so the banner can never become undismissable.
        stickyApprovalConflict.current = {
          message, payId, contextId: approval.contextId, calculationHash: approval.calculationHash,
        };
        // force: true so this is not collapsed into a refresh that is already in flight and may have passed its
        // approval block. It chains after any in-flight refresh and then re-enters, so it is a narrowing rather
        // than an absolute guarantee. The refresh evaluates retainedApprovalConflict against the context it
        // actually loaded, re-asserting the reason while that context is current and dropping it once it is not.
        // Re-asserting from the ref here instead would resurrect the message for an already-replaced context.
        await refreshSurface({ reloadCompliance: true, force: true });
      }
      void refreshBankingAfterFailedProgression(failure);
    } finally {
      setApprovalBusy(false);
    }
  };

  const finalise = async () => {
    if (lifecycleBusy || !payRun.meta.actions.includes('finalise')) return;
    setFinalisationBusy(true);
    setFinalisationError(null);

    let finalisation: PayRunFinalisationDto;
    try {
      finalisation = await finalisePayRun(payId);
    } catch (failure) {
      // The only genuine finalisation failure path. No ABA download is attempted.
      setFinalisationError(safeActionError(failure, true));
      setFinalisationDialogOpen(false);
      setFinalisationBusy(false);
      void refreshBankingAfterFailedProgression(failure);
      return;
    }

    /*
     * Hard success boundary. finalisePayRun has returned, so finalisation is committed and authoritative.
     * Nothing after this point may present the Pay Run as unfinalised: a later refresh or download failure is
     * its own follow-up problem and must never re-enter the finalisation-error path above.
     */
    setPayRun((current) => current ? { ...current, workflowStatus: 'complete', finalisation } : current);
    setFinalisationDialogOpen(false);

    try {
      await refreshSurface({ reloadEmployees: true, reloadCompliance: true, force: true });
    } catch {
      // Refresh-only problem. The Pay Run stays finalised and the ABA download still runs below.
      setActionNotice({
        tone: 'critical',
        title: 'Pay Run finalised; refresh required',
        message: 'The Pay Run was finalised, but current details could not be refreshed.',
        refreshRequired: true,
      });
    } finally {
      setFinalisationBusy(false);
    }

    /*
     * Independent follow-up: its outcome is reported separately and never changes the finalised result.
     * Only attempted once the EFT leg itself has succeeded and reconciled, because the server refuses a
     * download until then; a still-processing or failed leg would otherwise guarantee a 409.
     */
    if (eftApplies && finalisation.legs.some(isEftLegAbaReady))
      await downloadAba(payRun.businessRecordId, true);
  };

  /**
   * PRR-317. Deliberately a separate operation from finalisation: it runs only after finalisation has already
   * succeeded, and a failure here never touches finalisation, its evidence or the Pay Run lifecycle.
   */
  const downloadAba = async (businessRecordId: string, automatic: boolean): Promise<void> => {
    if (abaInFlight.current) return;
    abaInFlight.current = true;
    setAbaBusy(true);
    try {
      const { blob, fileName } = await downloadPayRunAbaFile(businessRecordId, payId);
      downloadBlob(blob, fileName);
      setAbaNotice({ tone: 'positive', title: 'ABA file downloaded', message: `Your browser downloaded ${fileName}.` });
    } catch (failure) {
      setAbaNotice({
        tone: 'critical',
        title: automatic ? 'Pay Run finalisation succeeded, but the ABA file could not be downloaded' : 'The ABA file could not be downloaded',
        // The download's conflict reasons are curated, non-sensitive and actionable, so they are preserved.
        message: `${safeActionError(failure, true)} Use Download ABA again to retry.`,
      });
    } finally {
      abaInFlight.current = false;
      setAbaBusy(false);
    }
  };

  const retryFanOut = async (legType: string) => {
    if (fanOutBusyLeg) return;
    setFanOutBusyLeg(legType);
    setFinalisationError(null);
    try {
      const finalisation = await retryPayRunFanOutLeg(payId, legType);
      setPayRun((current) => current ? { ...current, finalisation } : current);
    } catch (failure) {
      setFinalisationError(safeActionError(failure));
    } finally {
      setFanOutBusyLeg(null);
    }
  };

  const recoverFanOut = async () => {
    if (finalisationBusy || !payRun.finalisation?.canRecover) return;
    setFinalisationBusy(true);
    setFinalisationError(null);
    try {
      const finalisation = await recoverPayRunFinalisation(payId);
      setPayRun((current) => current ? { ...current, finalisation } : current);
    } catch (failure) {
      setFinalisationError(safeActionError(failure));
    } finally {
      setFinalisationBusy(false);
    }
  };

  return (
    <div className="mgo-stack mgo-gap-l" style={{ padding: 'var(--mgo-space-60)' }}>
      <a
        href="/payroll/pay-runs"
        className="mgo-cluster mgo-gap-xs mgo-text-s"
        style={{ color: 'var(--mgo-color-fg-link)', textDecoration: 'none', alignSelf: 'flex-start' }}
      >
        <Icon name="back" /> Pay Runs
      </a>
      {transitionError && <Callout tone="critical" title="Pay Run transition failed">{transitionError}</Callout>}
      {actionNotice && (
        <div
          role={actionNotice.tone === 'critical' ? 'alert' : 'status'}
          aria-live={actionNotice.tone === 'critical' ? 'assertive' : 'polite'}
          tabIndex={-1}
          data-testid="pay-run-action-result"
        >
          <Callout
            tone={actionNotice.tone}
            title={actionNotice.title}
            action={actionNotice.refreshRequired
              ? <Button label="Retry refresh" size="small" variant="secondary" loading={actionBusy} onClick={() => void retryActionRefresh()} />
              : undefined}
          >
            {actionNotice.message}
          </Callout>
        </div>
      )}
      <PayRunDetailTitle
        payRun={payRun}
        action={primaryAction}
        actionBusy={lifecycleBusy}
        secondaryActions={secondaryActions}
        onAction={(verb) => {
          if (verb === 'make_payroll_active') void applyTransition(verb);
          else if (verb === 'request_readiness') void requestReadiness();
          else if (verb === 'submit_approval') void submitApproval();
          else if (verb === 'review_banking') openBankingFindings();
          else if (!lifecycleBusy) setFinalisationDialogOpen(true);
        }}
        onSecondaryAction={(verb) => {
          if (verb === 'edit') openEdit();
          else if (verb === 'cancel') openCancel('header');
          else void applyTransition(verb);
        }}
      />

      <PayRunTimeline
        payRun={payRun}
        approval={approval}
        exceptions={exceptions}
        banking={bankingInput}
        busy={lifecycleBusy}
        showOperatingState={false}
        onAction={(verb) => {
          if (verb === 'request_readiness') void requestReadiness();
          else if (verb === 'submit_approval') void submitApproval();
          else if (verb === 'review_banking') openBankingFindings();
          else if (!lifecycleBusy) setFinalisationDialogOpen(true);
        }}
      />

      <PayRunDetailSummary payRun={payRun} populationStatus={status} leadCards={leadCards} />

      <div className="mgo-grid mgo-grid-cols-tablet-12 mgo-gap-l" data-testid="pay-run-detail-layout">
        <div className={showHeaderAndLifecycle && safeState ? 'mgo-col-span-tablet-8' : 'mgo-col-span-tablet-12'} data-testid="pay-run-detail-main">
          <PayRunDetailHeader
            payRun={payRun}
            transitioning={transitioning}
            actionBusy={actionLocked || approvalBusy || finalisationBusy}
            calculationBusy={calculationBusy}
            calculationError={calculationError}
            onCalculate={() => void calculate()}
            onRefreshCalculation={() => void refreshCalculation()}
            onLoadPopulation={() => setActiveDialog('load')}
          />
        </div>

        {showHeaderAndLifecycle && safeState && <aside className="mgo-col-span-tablet-4" data-testid="pay-run-detail-supporting">
          <PayRunLifecyclePanel
            payRun={payRun}
            approval={approval}
            exceptions={exceptions}
            busy={lifecycleBusy}
            approvalBusy={approvalBusy}
            finalisationBusy={finalisationBusy}
            onTransition={(verb) => void applyTransition(verb)}
            onCancel={openCancel}
            onSubmitApproval={() => void submitApproval()}
            onRequestReadiness={() => void requestReadiness()}
            onFinalise={() => {
              if (!lifecycleBusy) setFinalisationDialogOpen(true);
            }}
          />
        </aside>}
      </div>

      <div className="mgo-stack mgo-gap-l" data-testid="pay-run-detail-full-width">

        {finalisationError && !payRun.finalisation && (
          <Callout tone="critical" title="Pay Run finalisation failed">{finalisationError}</Callout>
        )}

        {payRun.finalisation && (
          <><PayRunFinalisationPanel
            finalisation={payRun.finalisation}
            busyLeg={fanOutBusyLeg}
            error={finalisationError}
            onRetry={(legType) => void retryFanOut(legType)}
            onRecover={() => void recoverFanOut()}
            abaAvailable={eftApplies}
            abaBusy={abaBusy}
            abaNotice={abaNotice}
            onDownloadAba={() => void downloadAba(payRun.businessRecordId, false)}
          />
          <PayAdvicePreparationPanel payId={payId} finalisationId={payRun.finalisation.finalisationId} /></>
        )}

        {/* The approval panel only renders once a context is submitted or decided, so a failure raised before that
            point (notably a 409 from Submit for authorisation) would otherwise have nowhere to surface. */}
        {!approvalPanelVisible && approvalError && ['ready_to_pay', 'eft_ready', 'cancelled'].includes(payRun.workflowStatus) && (
          <Callout
            tone="critical"
            title={approvalErrorKind === 'action' ? 'Approval action failed' : 'Approval details unavailable'}
            action={<Button label="Retry" size="small" variant="secondary" onClick={() => { forgetApprovalConflict(); void loadHeader(); }} />}
          >
            {approvalError}
          </Callout>
        )}

        {approvalPanelVisible && approval && (
          <PayRunApprovalPanel
            approval={approval}
            busy={approvalBusy}
            error={approvalError}
            onDecision={decideApproval}
          />
        )}

        <TabGroup value={activeTab} onValueChange={changeTab}>
          <Tab value="employees" label="Employees" leftIcon={<Icon name="person" />}>
            <PayRunEmployeesTab
              key={`employees-${populationReloadKey}`}
              payId={payId}
              workflowStatus={payRun.workflowStatus}
              calculationStatus={payRun.calculation?.status ?? 'not_ready'}
              populationStatus={status?.loadStatus}
              canRefreshPopulation={payRun.meta.actions.includes('refresh_population')}
              onRefreshAccepted={refreshAccepted}
            />
          </Tab>
          <Tab
            value="exceptions"
            label="Compliance"
            leftIcon={<Icon name="status-caution" />}
            badge={exceptions?.summary.total ?? 0}
            badgeTone={(exceptions?.summary.blocking ?? 0) > 0 ? 'critical' : 'caution'}
          >
            <PayRunExceptionsTab
              key={`exceptions-${populationReloadKey}`}
              payId={payId}
              businessRecordId={payRun.businessRecordId}
              banking={banking}
              bankingLoading={bankingLoading}
              bankingError={bankingError}
              bankingNotEvaluated={bankingNotEvaluated}
              onReloadBanking={loadBanking}
              onNotice={(notice) => setActionNotice(notice)}
              populationStatus={status?.loadStatus}
              reloadKey={readinessReloadKey}
              // reloadCompliance advances readinessReloadKey, which is what re-reads the shared Banking evidence so the
              // Review timeline stage reflects an override instead of going stale.
              onFindingActioned={async () => { await refreshSurface({ force: true, reloadCompliance: true }); }}
            />
          </Tab>
          <Tab value="audit" label="Audit trail" leftIcon={<Icon name="history" />}>
            <PayRunAuditTrailTab businessRecordId={payRun.businessRecordId} payId={payId} />
          </Tab>
        </TabGroup>
      </div>

      <AlertDialog
        testId="finalise-pay-run-dialog"
        visible={finalisationDialogOpen}
        severity="warning"
        header="Finalise this Pay Run?"
        confirmLabel="Finalise Pay Run"
        confirmLoading={finalisationBusy}
        confirmDisabled={!payRun.meta.actions.includes('finalise')}
        onConfirm={() => void finalise()}
        onCancel={() => setFinalisationDialogOpen(false)}
      >
        This irreversibly completes the authoritative payroll result and starts six simulated downstream outputs.
        Downstream failures will remain visible without undoing finalisation.
      </AlertDialog>

      <EditPayRunModal
        payRun={payRun}
        visible={activeDialog === 'edit'}
        displayLabel={displayLabel}
        notes={notes}
        busy={actionBusy}
        actionAllowed={payRun.meta.actions.includes('edit')}
        error={actionError}
        fieldErrors={fieldErrors}
        onDisplayLabelChange={setDisplayLabel}
        onNotesChange={setNotes}
        onSave={() => void saveEdit()}
        onClose={closeDialog}
      />
      <CancelPayRunModal
        payRun={payRun}
        visible={activeDialog === 'cancel'}
        busy={actionBusy}
        actionAllowed={payRun.meta.actions.includes('cancel')}
        error={actionError}
        onConfirm={() => void confirmCancel()}
        onClose={closeDialog}
      />
      <PayRunDataLoadModal
        payId={payId}
        visible={activeDialog === 'load'}
        onHide={() => {
          setActiveDialog(null);
          void reloadAfterDataLoad();
        }}
        onAccepted={refreshAccepted}
      />

    </div>
  );
};
